/**
 * Mobile Login Form Component for Rider Gateway
 *
 * Pre-OAuth surface — landing card with brand copy and the single CTA that
 * bounces to the hosted IdP. Composed against the lime/midnight palette so
 * it matches the rest of the rider portal.
 */

import type { FC } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAppSelector } from '../../hooks/reduxHooks';
import { selectAuthError, selectIsLoading } from '../../store/slices/authSlice';
import { r } from '../../theme/riderTheme';
import MobileLoginButton from '../atoms/MobileLoginButton';

interface MobileLoginFormProps {
  title?: string;
  subtitle?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.section`
  width: 100%;
  max-width: 380px;
  padding: 28px 26px 24px;
  background: ${({ theme }) => r(theme).surface.sheet};
  color: ${({ theme }) => r(theme).text.primary};
  border-radius: 20px;
  box-shadow:
    0 1px 0 ${({ theme }) => r(theme).surface.edge},
    0 20px 60px -20px rgba(0, 0, 0, 0.45);
  animation: ${fadeIn} 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const Eyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 8px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-family: 'Geist', sans-serif;
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.05;
  color: ${({ theme }) => r(theme).text.primary};
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => r(theme).text.muted};
`;

const ErrorBox = styled.div`
  position: relative;
  margin-bottom: 18px;
  padding: 12px 14px 12px 16px;
  border-radius: 12px;
  background: rgba(255, 25, 35, 0.06);
  border: 1px solid rgba(255, 25, 35, 0.32);
  color: ${({ theme }) => r(theme).accent.alert};
  font-family: 'Geist', sans-serif;
  font-size: 12.5px;
  line-height: 1.45;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 12px;
    bottom: 12px;
    width: 3px;
    border-radius: 0 2px 2px 0;
    background: ${({ theme }) => r(theme).accent.alert};
  }
`;

const ErrorHeading = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).accent.alert};
  margin-bottom: 4px;
`;

const Loading = styled.div`
  margin: 12px 0 6px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Spinner = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1.5px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: spin 720ms linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Hint = styled.p`
  margin: 16px 0 0;
  font-family: 'Geist', sans-serif;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => r(theme).text.muted};
  text-align: center;
`;

const MobileLoginForm: FC<MobileLoginFormProps> = ({
  title = 'Welcome back',
  subtitle = 'Sign in to manage your scooter, packs, and wallet.',
}) => {
  const isLoading = useAppSelector(selectIsLoading);
  const authError = useAppSelector(selectAuthError);

  return (
    <Card>
      <Eyebrow>Rider · Sign in</Eyebrow>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>

      {authError && (
        <ErrorBox role="alert">
          <ErrorHeading>Authentication error</ErrorHeading>
          {authError.message || 'Authentication failed. Please try again.'}
        </ErrorBox>
      )}

      {isLoading && (
        <Loading>
          <Spinner aria-hidden />
          <span>Redirecting securely…</span>
        </Loading>
      )}

      <MobileLoginButton disabled={isLoading} />

      <Hint>
        Use your account credentials to access your rider dashboard. Need help? Contact support.
      </Hint>
    </Card>
  );
};

export default MobileLoginForm;
